import { Injectable, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { EnterpriseModel } from '@models/enterprise/enterprise.model';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { HttpService } from './http.service';

const STORAGE_KEY = 'enterprises_cache';
const STORAGE_KEY_TS = 'enterprises_cache_ts';
const CACHE_AGE = 60 * 1000 * 5; // 5 minutes

@Injectable({
  providedIn: 'root',
})
export class EnterpriseService {
  private readonly enterprisesApi = `${environment.caisseApiUrl}/enterprises`;
  private readonly httpService = inject(HttpService);

  private readonly enterprises = signal<EnterpriseModel[]>(this.#readStorage());
  private readonly enterpriseLastUpdate = signal<number>(
    Number(localStorage.getItem(STORAGE_KEY_TS) ?? 0),
  );
  private inFlight$: Observable<EnterpriseModel> | null = null;

  readonly currentEnterprise = signal<EnterpriseModel | null>(this.#readStorage()[0] ?? null);

  getEnterprise(enterpriseId: number): Observable<EnterpriseModel> {
    if (this.enterpriseLastUpdate() > Date.now() - CACHE_AGE) {
      const cached = this.enterprises().find((e) => e.id === enterpriseId);
      if (cached) {
        return of(cached);
      }
    }
    return this.loadEnterprise(enterpriseId);
  }

  loadEnterprise(enterpriseId: number): Observable<EnterpriseModel> {
    if (!this.inFlight$) {
      this.inFlight$ = this.httpService
        .get<EnterpriseModel>(`${this.enterprisesApi}/${enterpriseId}`)
        .pipe(
          tap((data) => {
            const enterprises = [...this.enterprises().filter((e) => e.id !== data.id), data];
            this.enterprises.set(enterprises);
            this.currentEnterprise.set(data);
            this.enterpriseLastUpdate.set(Date.now());
            this.inFlight$ = null;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(enterprises));
            localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
          }),
          shareReplay(1),
        );
    }
    return this.inFlight$;
  }

  #readStorage(): EnterpriseModel[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const enterprises = JSON.parse(raw) as EnterpriseModel[];
      return enterprises.filter(
        (enterprise, index) => enterprises.findIndex((e) => e.id === enterprise.id) === index,
      );
    } catch {
      return [];
    }
  }
}
